/**
 * Legacy WordPress (mostrandomexico.com) → current app (mostrandomexico.mx)
 * URL compatibility layer.
 *
 * Pure, client-safe helpers only: no network, no DB. Routes consume these to
 * emit a single permanent redirect instead of a 404 / soft-404.
 *
 * Legacy shapes covered:
 *   /tours/{legacySlug}/
 *   /tour-destination/{destination}/
 *   /tour-destination/{destination}/page/{n}/
 *   /vuelos/
 */

import { CITY_CATALOG } from "./tour-search";

/** Confirmed one-to-one equivalences (legacy product slug → current slug). */
export const LEGACY_TOUR_ALIASES: Readonly<Record<string, string>> = {
  "excursion-tulum-akumal": "excursion-a-tulum-y-akumal-1238534",
  "chichen-itza-valladolid-cenote-xcajum":
    "chichen-itza-completo-cenotes-suytun-ik-kil-valladolid-1237816",
  // Beach-club product on the Bacalar lagoon — NOT the "siete colores" tour
  // (1238529) nor the express adventure (1238524).
  "bacalar-laguna-clubdeplaya": "bacalar-laguna-club-de-playa-1237954",
};

/** Legacy `tour-destination` slugs that don't match a city slug 1:1. */
const LEGACY_DESTINATION_ALIASES: Readonly<Record<string, string>> = {
  cancun: "cancun",
  "riviera-maya": "playa-del-carmen",
  "playa-del-carmen": "playa-del-carmen",
  playacar: "playa-del-carmen",
  tulum: "tulum",
  cozumel: "cozumel",
  "isla-mujeres": "isla-mujeres",
  holbox: "holbox",
  bacalar: "bacalar",
  "chichen-itza": "chichen-itza",
  valladolid: "valladolid",
  merida: "merida",
  yucatan: "merida",
  oaxaca: "oaxaca",
  cdmx: "cdmx",
  "ciudad-de-mexico": "cdmx",
  "mexico-city": "cdmx",
  "los-cabos": "los-cabos",
  cabos: "los-cabos",
  "puerto-vallarta": "puerto-vallarta",
  vallarta: "puerto-vallarta",
};

/** Normalize a legacy path segment: lowercase, accent-folded, slug-safe. */
export function normalizeLegacySegment(raw: string): string {
  return decodeURIComponent(raw ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Human-readable search query derived from a legacy slug. */
export function legacySlugToQuery(slug: string): string {
  const cleaned = normalizeLegacySegment(slug)
    // Drop WordPress noise and trailing ids so the query stays meaningful.
    .replace(/-\d{4,}$/, "")
    .split("-")
    .filter((w) => w.length > 1 && !/^\d+$/.test(w));
  return cleaned.join(" ").trim();
}

export type LegacyTarget =
  | { kind: "tour"; slug: string }
  | { kind: "city"; city: string }
  | { kind: "catalog"; q: string };

/** Resolve a legacy `/tours/{slug}` value using only the explicit alias map. */
export function legacyTourAlias(slug: string): string | null {
  return LEGACY_TOUR_ALIASES[normalizeLegacySegment(slug)] ?? null;
}

/**
 * Best-effort resolution of a legacy product slug against the live catalog
 * slugs. Matches when every meaningful token of the legacy slug appears in a
 * catalog slug; the candidate with most matched tokens wins.
 */
export function matchLegacySlugInCatalog(
  legacySlug: string,
  catalogSlugs: readonly string[],
): string | null {
  const tokens = legacySlugToQuery(legacySlug)
    .split(" ")
    .filter((t) => t.length > 2);
  if (tokens.length === 0) return null;

  let best: { slug: string; score: number } | null = null;
  for (const slug of catalogSlugs) {
    const norm = normalizeLegacySegment(slug);
    if (norm === normalizeLegacySegment(legacySlug)) return slug;
    const score = tokens.filter((t) => norm.includes(t)).length;
    if (score === tokens.length && (!best || score > best.score)) {
      best = { slug, score };
    }
  }
  return best?.slug ?? null;
}

/** Target for a legacy `/tour-destination/{destination}` path. */
export function legacyDestinationTarget(
  destination: string,
): { kind: "city"; city: string } | { kind: "catalog"; q: string } {
  const seg = normalizeLegacySegment(destination);
  const city = LEGACY_DESTINATION_ALIASES[seg] ?? (CITY_CATALOG[seg] ? seg : null);
  if (city && CITY_CATALOG[city]) return { kind: "city", city };
  const q = legacySlugToQuery(seg);
  return { kind: "catalog", q };
}

/** Target for a legacy `/tours/{slug}` path given the known catalog slugs. */
export function legacyTourTarget(
  legacySlug: string,
  catalogSlugs: readonly string[] = [],
): LegacyTarget {
  const alias = legacyTourAlias(legacySlug);
  if (alias) return { kind: "tour", slug: alias };

  const seg = normalizeLegacySegment(legacySlug);
  // A legacy /tours/{city} URL is a destination listing, not a product.
  if (CITY_CATALOG[seg]) return { kind: "city", city: seg };

  const matched = matchLegacySlugInCatalog(seg, catalogSlugs);
  if (matched) return { kind: "tour", slug: matched };

  return { kind: "catalog", q: legacySlugToQuery(seg) };
}
